import { useState } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtNum, fmtPct } from "@/lib/app";
import PinButton from "@/components/PinButton";
import PeriodOverrideBar from "@/components/PeriodOverrideBar";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

const fetcher = (url) => api.get(url).then((r) => r.data);

function Kpi({ label, value, sub, tone = "neutral", pinProps }) {
  const toneCls =
    tone === "good" ? "text-[var(--success)]" : tone === "bad" ? "text-[var(--critical)]" : tone === "warn" ? "text-[var(--warning)]" : "text-[var(--text-primary)]";
  return (
    <div className="card-flat p-5 relative group">
      {pinProps && (
        <div className="absolute top-3 right-3">
          <PinButton {...pinProps} testId={`pin-kpi-${pinProps.data?.metric || "x"}`} />
        </div>
      )}
      <p className="label-eyebrow mb-2">{label}</p>
      <p className={`font-display text-3xl font-bold tabular tracking-tight ${toneCls}`}>{value}</p>
      {sub && <p className="text-xs text-[var(--text-secondary)] mt-1 tabular">{sub}</p>}
    </div>
  );
}

function tooltipFormatter(value) {
  return [fmtEur(value)];
}

export default function Overview({ storeId, period }) {
  const [localPeriod, setLocalPeriod] = useState(period);
  const qs = `period_from=${localPeriod?.from || ""}&period_to=${localPeriod?.to || ""}`;
  const { data } = useSWR(`/stores/${storeId}/analytics/overview?${qs}`, fetcher);

  if (!data) return (
    <div className="space-y-3">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie prehľadu" testIdPrefix="overview-period" />
      <div className="text-[var(--text-secondary)]">Načítavam...</div>
    </div>
  );
  if (data.empty) return (
    <div className="space-y-3">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie prehľadu" testIdPrefix="overview-period" />
      <div className="card-flat p-8 text-center text-[var(--text-secondary)]">V tomto období nie sú dáta.</div>
    </div>
  );

  const t = data.totals;
  const effTone = t.efficiency_pct >= 100 ? "good" : t.efficiency_pct >= 70 ? "warn" : "bad";
  const pin = (label, value, key) => ({ storeId, kind: "kpi", label: `${label}: ${value}`, data: { metric: key, value: t[key], period: localPeriod }, period: localPeriod });

  return (
    <div className="space-y-6">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie prehľadu" testIdPrefix="overview-period" />
      <div className="card-flat p-4 text-sm text-[var(--text-secondary)] flex items-start gap-3 bg-[#F4F7F5] border-l-4 border-[var(--brand)]">
        <span className="text-[var(--brand)] mt-0.5">📌</span>
        <p>
          <strong>Označuj zaujímavé čísla:</strong> kliknite na zelenú ikonu pripináčika v rohu KPI karty,
          alebo pri riadkoch v sekciách <strong>Efektívnosť</strong> a <strong>Anomálie</strong>. Označené
          položky uvidíte v záložke „Označené body&rdquo; a AI ich zoberie do správy + idú do PDF/Excel exportu.
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi label="Celková tržba" value={fmtEur(t.revenue)} sub={`${t.unique_days} dní`} pinProps={pin("Celková tržba", fmtEur(t.revenue), "revenue")} />
        <Kpi label="Priem. denná tržba" value={fmtEur(t.avg_daily_revenue)} pinProps={pin("Priemerná denná tržba", fmtEur(t.avg_daily_revenue), "avg_daily_revenue")} />
        <Kpi
          label="Osobohodiny spolu"
          value={`${fmtNum(t.person_hours, 0)} h`}
          sub={t.unique_days ? `~${fmtNum(t.person_hours / t.unique_days, 1)} h/deň` : null}
          pinProps={pin("Osobohodiny spolu", `${fmtNum(t.person_hours, 0)} h`, "person_hours")}
        />
        <Kpi label="€ / osobohodina" value={fmtEur(t.avg_per_person_hour)} sub={`požad. ${fmtEur(t.required_performance)}`} tone={effTone} pinProps={pin("€ / osobohodina", fmtEur(t.avg_per_person_hour), "avg_per_person_hour")} />
        <Kpi label="Efektívnosť" value={fmtPct(t.efficiency_pct)} sub="oproti požadovanému výkonu" tone={effTone} pinProps={pin("Efektívnosť", fmtPct(t.efficiency_pct), "efficiency_pct")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card-flat p-5">
          <div className="flex items-start justify-between mb-1 gap-2">
            <h3 className="font-display text-base font-semibold">Priem. denná tržba podľa dňa v týždni</h3>
            <PinButton storeId={storeId} kind="chart" label={`Graf: Priem. denná tržba podľa dňa v týždni (${period.from} → ${period.to})`} data={{ id: "weekday-daily-revenue", period }} period={period} testId="pin-chart-weekday-daily" />
          </div>
          <p className="text-xs text-[var(--text-secondary)] mb-4">Priemer cez všetky výskyty daného dňa v zvolenom období.</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_weekday} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5E6962" }} />
              <YAxis tick={{ fontSize: 11, fill: "#5E6962" }} tickFormatter={(v) => `${v}€`} />
              <Tooltip
                formatter={(v) => fmtEur(v)}
                contentStyle={{ background: "white", border: "1px solid #E4E8E5", borderRadius: 6, fontSize: 12 }}
              />
              <Bar dataKey="avg_daily_revenue" name="Tržba" fill="#2B4C38" radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card-flat p-5">
          <div className="flex items-start justify-between mb-1 gap-2">
            <h3 className="font-display text-base font-semibold">€ / osobohodina podľa dňa v týždni</h3>
            <PinButton storeId={storeId} kind="chart" label={`Graf: €/os.hod podľa dňa v týždni (${period.from} → ${period.to})`} data={{ id: "weekday-per-person-hour", period }} period={period} testId="pin-chart-weekday-pph" />
          </div>
          <p className="text-xs text-[var(--text-secondary)] mb-4">Červená čiara = požadovaný výkon {fmtEur(t.required_performance)}.</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_weekday} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5E6962" }} />
              <YAxis tick={{ fontSize: 11, fill: "#5E6962" }} tickFormatter={(v) => `${v}€`} />
              <Tooltip
                formatter={(v) => fmtEur(v)}
                contentStyle={{ background: "white", border: "1px solid #E4E8E5", borderRadius: 6, fontSize: 12 }}
              />
              <ReferenceLine y={t.required_performance} stroke="#BA3F2B" strokeDasharray="4 4" />
              <Bar dataKey="per_person_hour" name="€/os.hod" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {data.by_weekday.map((r, i) => {
                  const c = r.per_person_hour >= t.required_performance ? "#3E7B52" : r.per_person_hour >= t.required_performance * 0.7 ? "#C78220" : "#BA3F2B";
                  return <Cell key={i} fill={c} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-flat p-5">
        <div className="flex items-start justify-between mb-1 gap-2">
          <h3 className="font-display text-base font-semibold">Priem. tržba na otvorenú hodinu</h3>
          <PinButton storeId={storeId} kind="chart" label={`Graf: Priem. tržba na otvorenú hodinu (${period.from} → ${period.to})`} data={{ id: "weekday-revenue-per-open-hour", period }} period={period} testId="pin-chart-weekday-rphour" />
        </div>
        <p className="text-xs text-[var(--text-secondary)] mb-4">
          Tento graf zohľadňuje, že niektoré dni sú kratšie (napr. sobota pol dňa). Ukazuje výkon prevádzky <strong>na hodinu otvorenia</strong>, nie na celý deň.
        </p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.by_weekday} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5E6962" }} />
            <YAxis tick={{ fontSize: 11, fill: "#5E6962" }} tickFormatter={(v) => `${v}€`} />
            <Tooltip
              formatter={(v) => fmtEur(v)}
              contentStyle={{ background: "white", border: "1px solid #E4E8E5", borderRadius: 6, fontSize: 12 }}
            />
            <Bar dataKey="avg_revenue_per_open_hour" name="Tržba / otvorenú hodinu" fill="#C78220" radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h3 className="font-display text-base font-semibold">Detail podľa dňa v týždni</h3>
        </div>
        <table>
          <thead>
            <tr>
              <th>Deň</th>
              <th className="text-right">Tržba</th>
              <th className="text-right">Priem. denne</th>
              <th className="text-right">Os.hod</th>
              <th className="text-right">€/os.hod</th>
              <th className="text-right">Tržba/hod</th>
              <th className="text-right">Otv. hod/deň</th>
              <th className="text-right">Dní</th>
            </tr>
          </thead>
          <tbody>
            {data.by_weekday.map((r) => (
              <tr key={r.weekday}>
                <td className="font-medium">{r.name}</td>
                <td className="text-right tabular">{fmtEur(r.revenue)}</td>
                <td className="text-right tabular">{fmtEur(r.avg_daily_revenue)}</td>
                <td className="text-right tabular">{fmtNum(r.employees, 0)}</td>
                <td className="text-right tabular">{fmtEur(r.per_person_hour)}</td>
                <td className="text-right tabular font-medium">{fmtEur(r.avg_revenue_per_open_hour)}</td>
                <td className="text-right tabular text-[var(--text-secondary)]">{fmtNum(r.hours_open_per_day, 1)}</td>
                <td className="text-right tabular">{r.days}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
